import {
  Base64Param,
  BooleanParam,
  GetRouteMatchType,
  GetSetRouteMatchType,
  route,
  RouteMap,
  StringParam
} from '@st/util/routing'
import { clientAppBaseUrl } from '~/env'

import type { Param } from '@st/util/routing'
import { TypedParam } from '@st/util/routing'

type SchemaId = string

type SectionID = 'basic-info' | 'income' | 'deductions'

export const SchemaIdParam: Param<SchemaId> = {
  encode: (s: SchemaId) => s.toLowerCase(),
  decode: (s: SchemaId) => s.toLowerCase()
}

export const SectionIdParam = TypedParam<SectionID>()

export const rootRoute = route('/', '/&:code?', {
  code: StringParam
})

export const devRoute = route('dev', '/dev', {})

export const organizationWorkpaperLegacy = route(
  'organization_folder_workpaper_legacy',
  '/:organizationSlug/workpapers/:folderId',
  {
    organizationSlug: StringParam,
    folderId: StringParam
  }
)

export const organizationFolderRoute = route(
  'organization_folder',
  '/:organizationSlug/binders/:folderId',
  {
    organizationSlug: StringParam,
    folderId: StringParam
  }
)

export const debugRoute = route('debug', '/debug', {})

export const organizationFolderDebug = route(
  'organization_folder_debug',
  '/:organizationSlug/binders/:folderId/debug',
  {
    organizationSlug: StringParam,
    folderId: StringParam
  }
)

export const organizationPreviewQuestionnaire = route(
  'organization_preview_questionnaire',
  '/:organizationSlug/binders/:folderId/preview-organizer',
  {
    organizationSlug: StringParam,
    folderId: StringParam
  }
)

export const organizationSelectStackRoute = route(
  'organization_select_stack',
  '/:organizationSlug/stack',
  {
    organizationSlug: StringParam
  }
)

export const clientFolderRoute = route(
  'client_folder',
  '/:organizationSlug/folders/:folderId&:aud?',
  {
    organizationSlug: StringParam,
    folderId: StringParam,
    aud: StringParam
  }
)

export const oauthCallbackRoute = route('oauth_callback', '/oauth/callback/:provider&:code?', {
  provider: StringParam,
  code: StringParam
})

export const workpaperListLegacyRoute = route(
  'workpaper_list_legacy',
  '/:organizationSlug/workpapers',
  {
    organizationSlug: StringParam
  }
)

export const folderListRoute = route('folder_list', '/:organizationSlug/binders', {
  organizationSlug: StringParam
})

export const connectAppRoute = route(
  'connect_app',
  '/connect/:app_name&:client_id&:state&:redirect_uri',
  {
    app_name: StringParam,
    client_id: StringParam,
    state: StringParam,
    redirect_uri: StringParam
  }
)

export const createImportRoute = route('create_import', '/:organizationSlug/import&:preview?', {
  organizationSlug: StringParam,
  preview: BooleanParam
})

export const runImportRoute = route('run_import', '/:organizationSlug/import/:importId', {
  organizationSlug: StringParam,
  importId: StringParam
})

export const manageAccountRoute = route('account', '/:organizationSlug/settings/account', {
  organizationSlug: StringParam
})

export const manageOrganizationTagsRoute = route(
  'manage_organization_tags',
  '/:organizationSlug/settings/tags',
  {
    organizationSlug: StringParam
  }
)

export const manageOrganizationIntegrationsRoute = route(
  'manage_organization_integrations',
  '/:organizationSlug/settings/integrations',
  {
    organizationSlug: StringParam
  }
)

export const manageOrganizationMessageTemplatesRoute = route(
  'manage_organization_message_templates',
  '/:organizationSlug/settings/templates',
  {
    organizationSlug: StringParam
  }
)

export const organizationBalanceRoute = route(
  'organization_balance',
  '/:organizationSlug/settings/balance',
  {
    organizationSlug: StringParam
  }
)

export const brandingRoute = route('branding', '/:organizationSlug/settings/branding', {
  organizationSlug: StringParam
})

export const teamRoute = route('team', '/:organizationSlug/settings/team', {
  organizationSlug: StringParam
})

export const teamInvitation = route('team_invitation', '/:organizationSlug/invitation/:email', {
  organizationSlug: StringParam,
  email: Base64Param
})

export const loggedInPricing = route('logged_in_pricing', '/:organizationSlug/pricing', {
  organizationSlug: StringParam
})

export const loggedOutPricing = route('logged_out_pricing', '/pricing')

export const updatesRoute = route('updates', '/updates')

export const loginRoute = route('login', '/login')

export const signUpRoute = route(
  'sign_up',
  '/sign-up&:email?&:organizationName?&:phoneNumber?&:trial?',
  {
    email: StringParam,
    organizationName: StringParam,
    phoneNumber: StringParam,
    trial: BooleanParam
  }
)

export const clientFindOrCreateFolderRoute = route(
  'find_or_create_folder',
  '/:organizationSlug/questionnaire/:schemaId&:email?',
  {
    email: StringParam,
    organizationSlug: StringParam,
    schemaId: SchemaIdParam
  }
)

export const securityRoute = route('security', '/security')

const routesMap = {
  rootRoute,
  loginRoute,
  oauthCallbackRoute,
  signUpRoute,
  connectAppRoute,
  loggedOutPricing,
  securityRoute,
  devRoute,
  debugRoute,
  organizationPreviewQuestionnaire,
  organizationFolderDebug,
  organizationWorkpaperLegacy,
  organizationFolderRoute,
  workpaperListLegacyRoute,
  folderListRoute,
  manageAccountRoute,
  manageOrganizationTagsRoute,
  manageOrganizationMessageTemplatesRoute,
  organizationBalanceRoute,
  manageOrganizationIntegrationsRoute,
  brandingRoute,
  organizationSelectStackRoute,
  teamRoute,
  teamInvitation,
  loggedInPricing,
  runImportRoute,
  createImportRoute,
  clientFindOrCreateFolderRoute,
  clientFolderRoute
}
export const routes = new RouteMap(routesMap)

export type AppRoute = GetRouteMatchType<typeof routesMap>
export type SetAppRoute = GetSetRouteMatchType<typeof routesMap>

type ClientFolderShareableLink = {
  organizationSlug: string
  folderId: string
}
export function clientFolderShareableLink(link: ClientFolderShareableLink): string {
  return (
    clientAppBaseUrl() +
    clientFolderRoute({
      organizationSlug: link.organizationSlug,
      folderId: link.folderId
    })
  )
}

type MagicLink = {
  organizationSlug: string
  schemaId: string
}
export function clientMagicLink(link: MagicLink): string {
  return (
    clientAppBaseUrl() +
    clientFindOrCreateFolderRoute({
      organizationSlug: link.organizationSlug,
      schemaId: link.schemaId
    })
  )
}
